import constants from '@elab/core/utils/constants';

import { reduceAction } from '../helpers';

import types from './types';

export const initialState = {
    productCode: null,
    allReviews: [],
    currentReviews: null,
    currentAvisReviews: null,
    currentCount: 0,
    totalCount: 0,
    currentPage: 0,
    numberOfItemsPerPage: constants.numberOfReviewItemsPerPage,
    allReviewsLoaded: false,
    loading: false,
    activeFilters: [],
    activeFiltersCount: 0,
    isAvis: false,
    isPaginated: false,
    showReactions: false,
    reportedReviews: [],
    filtersMenuDisplayed: false,
    currentReview: {},
    translationFeatureOn: false,
    googleApiKey: '',
};

const reducers = (state = initialState, { type, payload }) => {
    switch (type) {
        case types.SET_PRODUCT_CODE:
            return reduceAction(state, 'productCode', payload);

        case types.INITIALIZE:
            return {
                ...state,
                totalCount: payload.totalCount,
                productCode: payload.productCode,
                sort: payload.sort,
                allReviews: payload.reviews,
                currentReviews: payload.reviews,
                currentCount: payload.currentCount,
                numberOfItemsPerPage: payload.numberOfItemsPerPage,
                isAvis: !!payload.isAvis,
                isPaginated: !!payload.isPaginated,
                showReactions: !!payload.showReactions,
                translationFeatureOn: !!payload.translationFeatureOn,
                // currentPage can be equal to 0, so the field existence check is required
                currentPage: 'currentPage' in payload ? payload.currentPage : state.currentPage,
            };

        case types.SET_REVIEWS: {
            const currentReviews = payload.results || [];
            return {
                ...state,
                allReviews: currentReviews,
                currentReviews,
                currentCount: currentReviews.length,
                totalCount: payload.pagination.totalResults,
                allReviewsLoaded: currentReviews.length === payload.pagination.totalResults,
                pagination: payload.pagination,
                isLoadMoreButtonShown: false,
                loadReviewsIteration: 1,
            };
        }

        case types.SET_MORE_REVIEWS: {
            const updatedReviews = [...state.allReviews, ...(payload.results || [])];
            return {
                ...state,
                allReviews: updatedReviews,
                currentReviews: updatedReviews,
                currentCount: updatedReviews.length,
                totalCount: payload.pagination.totalResults,
                allReviewsLoaded: updatedReviews.length === payload.pagination.totalResults,
                isLoadMoreButtonShown: payload.pagination.currentPage > 1,
            };
        }

        case types.INCREASE_LOAD_ITERATION: {
            return {
                ...state,
                loadReviewsIteration: payload,
            };
        }

        case types.SET_LOADING:
            return reduceAction(state, 'loading', payload);

        case types.SET_CURRENT_PAGE:
            return reduceAction(state, 'currentPage', payload);

        case types.SET_NUMBER_OF_ITEMS_PER_PAGE:
            return reduceAction(state, 'numberOfItemsPerPage', payload);

        case types.ADD_ACTIVE_FILTER: {
            const activeFilters = [
                ...state.activeFilters,
                { value: payload.value, label: payload.label, filterType: payload.filterType },
            ];
            return {
                ...state,
                activeFilters,
                activeFiltersCount: activeFilters.length,
                loadReviewsIteration: 1,
            };
        }
        case types.SET_ACTIVE_FILTER: {
            const newFilter = {
                value: payload.value,
                label: payload.label,
                filterType: payload.filterType,
            };
            return {
                ...state,
                activeFilters: [newFilter],
                activeFiltersCount: 1,
            };
        }

        case types.REMOVE_ACTIVE_FILTER: {
            const activeFilters = state.activeFilters.filter(item => item.value !== payload.value);
            return {
                ...state,
                activeFilters,
                activeFiltersCount: activeFilters.length,
            };
        }
        case types.REMOVE_ALL_FILTERS: {
            return {
                ...state,
                activeFilters: [],
                activeFiltersCount: 0,
            };
        }
        case types.SET_SORT_DEFAULT:
        case types.SET_SORT:
            return reduceAction(state, 'sort', payload);

        case types.SET_REPORTED_REVIEWS:
            return reduceAction(state, 'reportedReviews', payload || []);

        case types.SET_REACTION:
            return {
                ...state,
                currentReviews:
                    state.currentReviews &&
                    state.currentReviews.map(entry => {
                        if (entry.id === payload.id) {
                            if (payload.type === 'like') {
                                return {
                                    ...entry,
                                    like: !!payload.value,
                                    likes: payload.value ? entry.likes + 1 : entry.likes - 1,
                                };
                            }
                            return {
                                ...entry,
                                dislike: !!payload.value,
                                dislikes: payload.value ? entry.dislikes + 1 : entry.dislikes - 1,
                            };
                        }
                        return entry;
                    }),
            };

        case types.SET_LOADED_REACTIONS:
            return {
                ...state,
                currentReviews:
                    state.currentReviews &&
                    state.currentReviews.map(entry => {
                        return {
                            ...entry,
                            like: payload.likes.includes(entry.id),
                            dislike: payload.dislikes.includes(entry.id),
                        };
                    }),
            };

        case types.SET_SHOW_TRANSLATED_REVIEW: {
            const updatedReviews = state.allReviews.map(entry => {
                if (entry.id === payload.id) {
                    return {
                        ...entry,
                        showTranslatedReview: payload.value,
                    };
                }
                return entry;
            });
            return {
                ...state,
                allReviews: updatedReviews,
                currentReviews: updatedReviews,
            };
        }

        case types.SET_TRANSLATION_LOADING: {
            const updatedReviews = state.allReviews.map(entry => {
                if (entry.id === payload.id) {
                    return {
                        ...entry,
                        translationLoading: payload.value,
                    };
                }
                return entry;
            });
            return {
                ...state,
                allReviews: updatedReviews,
                currentReviews: updatedReviews,
            };
        }

        case types.SET_TRANSLATION: {
            const updatedReviews = state.allReviews.map(entry => {
                if (entry.id === payload.id) {
                    return {
                        ...entry,
                        translation: {
                            comment: payload.comment,
                            headline: payload.headline,
                        },
                    };
                }
                return entry;
            });
            return {
                ...state,
                allReviews: updatedReviews,
                currentReviews: updatedReviews,
            };
        }

        case types.SET_NEEDS_TRANSLATION: {
            const updatedReviews = state.allReviews.map(entry => {
                if (payload.has(entry.id)) {
                    return {
                        ...entry,
                        needsTranslation: payload.get(entry.id),
                    };
                }
                return entry;
            });
            return {
                ...state,
                allReviews: updatedReviews,
                currentReviews: updatedReviews,
            };
        }

        case types.SET_GOOGLE_API_KEY:
            return { ...state, googleApiKey: payload.googleApiKey };

        case types.SET_CURRENT_REVIEW:
            return { ...state, currentReview: payload };

        case types.REPORT_ENHANCED_REVIEW:
            return state;

        case types.SHOW_FILTERS_MENU:
            return {
                ...state,
                filtersMenuDisplayed: true,
            };

        case types.HIDE_FILTERS_MENU:
            return {
                ...state,
                filtersMenuDisplayed: false,
            };

        case types.RESET_CURRENT:
            return {
                ...state,
                currentReview: {},
            };

        default:
            return state;
    }
};

export default reducers;
